/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com
 
 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_TIMEOUT } from "utils";
import { logoutUser } from "actions/AuthAction";
import swal from "sweetalert";
import {
  getDatesWithoutWeekends,
  hitungtotaljamkerja,
  convertToMinutes,
  convertFromMinutes,
  WAKTU_KERJA5,
} from "utils/func";

function DetailKinerjaIndividu() {
  const { bidangId, uid_RSS } = useParams();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState({
    type: "", // 'laporan_harian', 'instruksi', 'disposisi', 'events', 'tamu'
    title: "",
    data: [],
    tanggal: "",
  });
  const [openKegiatanModal, setOpenKegiatanModal] = useState(false);
  const [selectedKegiatan, setSelectedKegiatan] = useState([]);
  const [selectedLaporanTitle, setSelectedLaporanTitle] = useState("");
  const [loadingKegiatan, setLoadingKegiatan] = useState(false);
  const [laporanHarian, setLaporanHarian] = useState([]);
  const [dataDaftarHadir, setDataDaftarHadir] = useState([]);
  const [dataHariLibur, setDataHariLibur] = useState([]);
  const [listLibur, setListLibur] = useState([]);
  const [pegawaiData, setPegawaiData] = useState(null);
  const [dateRange, setDateRange] = useState({
    tanggal_awal: "",
    tanggal_akhir: "",
  });
  const [filterDateRange, setFilterDateRange] = useState({
    tanggal_awal: "",
    tanggal_akhir: "",
  });

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      logoutUser(navigate);
      return;
    }

    // Fetch data pegawai
    fetchPegawaiData();

    // Fetch list libur
    getLibur();

    // Inisialisasi filter tanggal dengan tanggal 1 bulan ini sampai hari ini dengan WITA (UTC+8)
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

    const initialTanggalAwal = new Date(
      witaDate.getFullYear(),
      witaDate.getMonth(),
      1
    )
      .toISOString()
      .split("T")[0];
    const initialTanggalAkhir = witaDate.toISOString().split("T")[0];

    setFilterDateRange({
      tanggal_awal: initialTanggalAwal,
      tanggal_akhir: initialTanggalAkhir,
    });

    if (uid_RSS) {
      fetchLaporanHarian();
    }
  }, [bidangId, uid_RSS, navigate]);

  // Fungsi untuk mengambil data pegawai
  const fetchPegawaiData = async () => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/bidang/${bidangId}/pegawai`
      );
      const pegawai = response.data.find((p) => p.uid_RSS === uid_RSS);
      setPegawaiData(pegawai || null);
    } catch (error) {
      console.error("Error fetching pegawai data:", error);
    }
  };

  // Fungsi untuk mengambil data daftar hadir dari endpoint eksternal
  const fetchDaftarHadir = async (tanggal_awal, tanggal_akhir) => {
    if (!uid_RSS || !tanggal_awal || !tanggal_akhir) {
      return;
    }

    try {
      const response = await axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/getdh/mingguan",
        {
          tAwal: tanggal_awal,
          tAkhir: tanggal_akhir,
          idUser: uid_RSS,
        }
      );

      if (response.data && response.data.success === 1) {
        setDataDaftarHadir(response.data.data || []);
      } else {
        setDataDaftarHadir([]);
      }
    } catch (err) {
      console.error("Error fetching daftar hadir:", err);
      setDataDaftarHadir([]);
    }
  };

  // Fungsi untuk mengambil data hari libur dari endpoint eksternal
  const fetchHariLibur = async (tanggal_awal, tanggal_akhir) => {
    if (!tanggal_awal || !tanggal_akhir) {
      return;
    }

    try {
      const response = await axios.get(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/libur",
        {
          params: {
            tanggal_awal: tanggal_awal,
            tanggal_akhir: tanggal_akhir,
          },
        }
      );

      if (response.data && response.data.success === 1) {
        setDataHariLibur(response.data.data || []);
      } else {
        setDataHariLibur([]);
      }
    } catch (err) {
      console.error("Error fetching hari libur:", err);
      setDataHariLibur([]);
    }
  };

  // Fungsi untuk mengambil list libur (untuk perhitungan hari kerja)
  const getLibur = async () => {
    try {
      const response = await axios({
        method: "get",
        url: "https://smartservice.rotendaokab.go.id/api/admin/libur",
        timeout: API_TIMEOUT,
        headers: {
          Authorization: `Bearer ${""}`,
        },
      });

      if (response.status === 200) {
        setListLibur(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching libur data:", error);
      setListLibur([]);
    }
  };

  const fetchLaporanHarian = async (customDateRange = null) => {
    setLoading(true);

    let tanggal_awal, tanggal_akhir;

    if (customDateRange) {
      // Gunakan filter tanggal yang dipilih user
      tanggal_awal = customDateRange.tanggal_awal;
      tanggal_akhir = customDateRange.tanggal_akhir;
    } else {
      // Hitung tanggal awal (tanggal 1 bulan ini) dengan WITA (UTC+8)
      const today = new Date();
      const witaOffset = 8 * 60; // UTC+8 dalam menit
      const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

      tanggal_awal = new Date(witaDate.getFullYear(), witaDate.getMonth(), 1)
        .toISOString()
        .split("T")[0];

      // Hitung tanggal akhir (tanggal hari ini) dengan WITA
      tanggal_akhir = witaDate.toISOString().split("T")[0];
    }

    // Simpan rentang tanggal ke state
    setDateRange({
      tanggal_awal: tanggal_awal,
      tanggal_akhir: tanggal_akhir,
    });

    try {
      // Fetch semua data secara bersamaan
      const [laporanResponse] = await Promise.all([
        axios.get(`${API_ESA_OFFICE}laporan-harian/pegawai/${uid_RSS}`, {
          params: {
            tanggal_awal: tanggal_awal,
            tanggal_akhir: tanggal_akhir,
          },
        }),
        fetchDaftarHadir(tanggal_awal, tanggal_akhir),
        fetchHariLibur(tanggal_awal, tanggal_akhir),
      ]);

      // Data baru sudah dalam format per hari dengan laporan_harian, instruksi, disposisi, events, apel_pagi, apel_siang
      setLaporanHarian(laporanResponse.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching laporan harian:", err);
      // Tetap fetch daftar hadir dan hari libur meskipun laporan error
      await Promise.all([
        fetchDaftarHadir(tanggal_awal, tanggal_akhir),
        fetchHariLibur(tanggal_awal, tanggal_akhir),
      ]);
      setLoading(false);
      swal("Error", "Gagal mengambil data laporan harian", "error");
    }
  };

  const handleOpenKegiatanModal = (id_laporan, tanggal) => {
    setOpenKegiatanModal(true);
    setLoadingKegiatan(true);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle(
      `Kegiatan Laporan Harian - ${new Date(tanggal).toLocaleDateString(
        "id-ID"
      )}`
    );

    axios
      .get(`${API_ESA_OFFICE}laporan-harian/listkegiatan/${id_laporan}`)
      .then((res) => {
        setSelectedKegiatan(res.data.kegiatan || []);
        setLoadingKegiatan(false);
      })
      .catch((err) => {
        console.error("Error fetching kegiatan:", err);
        setSelectedKegiatan([]);
        setLoadingKegiatan(false);
      });
  };

  const handleCloseKegiatanModal = () => {
    setOpenKegiatanModal(false);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle("");
    setLoadingKegiatan(false);
  };

  const handleOpenDetailModal = (type, data, tanggal) => {
    let title = "";
    switch (type) {
      case "laporan_harian":
        title = `Detail Laporan Harian - ${new Date(tanggal).toLocaleDateString(
          "id-ID"
        )}`;
        break;
      case "instruksi":
        title = `Detail Instruksi Selesai - ${new Date(
          tanggal
        ).toLocaleDateString("id-ID")}`;
        break;
      case "disposisi":
        title = `Detail Disposisi Selesai - ${new Date(
          tanggal
        ).toLocaleDateString("id-ID")}`;
        break;
      case "events":
        title = `Detail Event - ${new Date(tanggal).toLocaleDateString(
          "id-ID"
        )}`;
        break;
      case "tamu":
        title = `Detail Tamu - ${new Date(tanggal).toLocaleDateString(
          "id-ID"
        )}`;
        break;
      default:
        title = "Detail";
    }
    setDetailModalData({
      type: type,
      title: title,
      data: data || [],
      tanggal: tanggal,
    });
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setDetailModalData({
      type: "",
      title: "",
      data: [],
      tanggal: "",
    });
  };

  const handleDateRangeChange = (field, value) => {
    const newDateRange = {
      ...filterDateRange,
      [field]: value,
    };

    setFilterDateRange(newDateRange);

    // Validasi tanggal
    if (newDateRange.tanggal_awal && newDateRange.tanggal_akhir) {
      if (newDateRange.tanggal_awal > newDateRange.tanggal_akhir) {
        swal(
          "Error",
          "Tanggal awal tidak boleh lebih besar dari tanggal akhir",
          "error"
        );
        return;
      }

      // Trigger fetch data dengan filter baru
      fetchLaporanHarian(newDateRange);
    }
  };

  const handleResetFilter = () => {
    // Reset filter tanggal ke default (tanggal 1 bulan ini sampai hari ini) dengan WITA (UTC+8)
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

    const resetTanggalAwal = new Date(
      witaDate.getFullYear(),
      witaDate.getMonth(),
      1
    )
      .toISOString()
      .split("T")[0];
    const resetTanggalAkhir = witaDate.toISOString().split("T")[0];

    const resetDateRange = {
      tanggal_awal: resetTanggalAwal,
      tanggal_akhir: resetTanggalAkhir,
    };

    setFilterDateRange(resetDateRange);
    fetchLaporanHarian(resetDateRange);
  };

  // Fungsi untuk menormalisasi tanggal ke format YYYY-MM-DD
  const normalizeDate = (date) => {
    if (!date) return null;
    if (typeof date === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    }
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return null;
  };

  // Fungsi untuk generate semua tanggal dari tanggal_awal sampai tanggal_akhir
  const generateAllDates = (tanggal_awal, tanggal_akhir) => {
    if (!tanggal_awal || !tanggal_akhir) return [];

    const dates = [];
    const startDate = new Date(tanggal_awal);
    const endDate = new Date(tanggal_akhir);

    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  // Merge data laporan harian dengan semua tanggal yang di-generate
  const filteredLaporanHarian = useMemo(() => {
    if (!dateRange.tanggal_awal || !dateRange.tanggal_akhir) {
      return laporanHarian;
    }

    // Generate semua tanggal dari tanggal_awal sampai tanggal_akhir
    const allDates = generateAllDates(
      dateRange.tanggal_awal,
      dateRange.tanggal_akhir
    );

    // Create map dari laporan harian berdasarkan tanggal
    const laporanMap = new Map();
    laporanHarian.forEach((item) => {
      const tanggalNormalized = normalizeDate(item.tanggal);
      if (tanggalNormalized) {
        laporanMap.set(tanggalNormalized, item);
      }
    });

    // Merge semua tanggal dengan data laporan harian
    const mergedData = allDates.map((date) => {
      const tanggalNormalized = normalizeDate(date.toISOString().split("T")[0]);
      const laporanData = laporanMap.get(tanggalNormalized);

      if (laporanData) {
        return laporanData;
      } else {
        // Return empty data structure untuk hari yang tidak ada laporan
        return {
          tanggal: tanggalNormalized,
          laporan_harian: [],
          instruksi: [],
          disposisi: [],
          events: [],
          tamu: [],
          apel_pagi: null,
          apel_siang: null,
        };
      }
    });

    // Sort dari tanggal terbaru ke terlama (descending)
    const sortedData = mergedData.sort((a, b) => {
      const dateA = new Date(a.tanggal);
      const dateB = new Date(b.tanggal);
      return dateB - dateA; // Terbaru ke terlama
    });

    return sortedData;
  }, [laporanHarian, dateRange, dataHariLibur]);

  // Hitung statistik aktivitas
  const statistikAktivitas = useMemo(() => {
    let totalHadir = 0;
    let totalApelPagi = 0;
    let totalApelSiang = 0;
    let totalDisposisi = 0;
    let totalInstruksi = 0;
    let totalTamu = 0;
    let totalEvent = 0;

    filteredLaporanHarian.forEach((item) => {
      // Hitung hadir dari dataDaftarHadir
      const tanggalNormalized = normalizeDate(item.tanggal);
      const daftarHadirData = dataDaftarHadir.find(
        (dh) => normalizeDate(dh.tanggal) === tanggalNormalized
      );
      if (daftarHadirData && daftarHadirData.status === "Hadir") {
        totalHadir++;
      }

      // Hitung apel pagi
      if (item.apel_pagi) {
        totalApelPagi++;
      }

      // Hitung apel siang
      if (item.apel_siang) {
        totalApelSiang++;
      }

      // Hitung disposisi
      if (item.disposisi && item.disposisi.length > 0) {
        totalDisposisi += item.disposisi.length;
      }

      // Hitung instruksi
      if (item.instruksi && item.instruksi.length > 0) {
        totalInstruksi += item.instruksi.length;
      }

      // Hitung tamu
      if (item.tamu && item.tamu.length > 0) {
        totalTamu += item.tamu.length;
      }

      // Hitung event
      if (item.events && item.events.length > 0) {
        totalEvent += item.events.length;
      }
    });

    return {
      totalHadir,
      totalApelPagi,
      totalApelSiang,
      totalDisposisi,
      totalInstruksi,
      totalTamu,
      totalEvent,
    };
  }, [filteredLaporanHarian, dataDaftarHadir]);

  // Fungsi untuk menghitung persentase jam kerja harian (target berdasarkan hari)
  const calculatePercentage = (workingHours, tanggal) => {
    if (!workingHours || workingHours === "0:00" || workingHours === "") {
      return 0;
    }

    // Konversi workingHours ke menit
    const [hours, minutes] = workingHours.split(":").map(Number);
    const workingMinutes = hours * 60 + minutes;

    // Tentukan target jam kerja berdasarkan hari dalam seminggu
    let targetJamKerja = "0:00";
    if (tanggal) {
      const dateObj = new Date(tanggal);
      const dayOfWeek = dateObj.getDay(); // 0 = Minggu, 1 = Senin, ..., 6 = Sabtu
      targetJamKerja = WAKTU_KERJA5[dayOfWeek] || "0:00";
    } else {
      // Fallback ke 8 jam jika tanggal tidak tersedia
      targetJamKerja = "8:00";
    }

    // Konversi target jam kerja ke menit
    const [targetHours, targetMins] = targetJamKerja.split(":").map(Number);
    const targetMinutes = targetHours * 60 + targetMins;

    if (targetMinutes === 0) return 0;

    const percentage = (workingMinutes / targetMinutes) * 100;
    return Math.round(percentage * 100) / 100; // Round to 2 decimal places
  };

  // Fungsi untuk mendapatkan target jam kerja berdasarkan tanggal
  const getTargetJamKerja = (tanggal) => {
    if (!tanggal) return "8:00"; // Fallback

    const dateObj = new Date(tanggal);
    const dayOfWeek = dateObj.getDay(); // 0 = Minggu, 1 = Senin, ..., 6 = Sabtu
    return WAKTU_KERJA5[dayOfWeek] || "0:00";
  };

  // Hitung statistik jam kerja
  const statistikJamKerja = useMemo(() => {
    if (
      !dateRange.tanggal_awal ||
      !dateRange.tanggal_akhir ||
      !listLibur.length
    ) {
      return {
        totalHariKerja: 0,
        targetJamKerja: "0:00",
        totalJamKerjaAktual: "0:00",
        persentasePencapaian: 0,
      };
    }

    // Hitung hari kerja (tanpa weekend dan libur)
    const hariKerja = getDatesWithoutWeekends(
      dateRange.tanggal_awal,
      dateRange.tanggal_akhir,
      listLibur
    );

    // Hitung target jam kerja total
    const targetJamKerjaTotal = hitungtotaljamkerja(hariKerja);

    // Hitung total jam kerja aktual dari data daftar hadir
    let totalJamKerjaAktualMinutes = 0;
    dataDaftarHadir.forEach((item) => {
      if (
        item.workingHours &&
        item.workingHours !== "0:00" &&
        item.workingHours !== ""
      ) {
        const [hours, minutes] = item.workingHours.split(":").map(Number);
        totalJamKerjaAktualMinutes += hours * 60 + minutes;
      }
    });

    const totalJamKerjaAktual = convertFromMinutes(totalJamKerjaAktualMinutes);

    // Hitung persentase pencapaian
    const targetMinutes = convertToMinutes(targetJamKerjaTotal);
    const persentasePencapaian =
      targetMinutes > 0
        ? Math.round((totalJamKerjaAktualMinutes / targetMinutes) * 100 * 100) /
          100
        : 0;

    return {
      totalHariKerja: hariKerja.length,
      targetJamKerja: targetJamKerjaTotal,
      totalJamKerjaAktual: totalJamKerjaAktual,
      persentasePencapaian: persentasePencapaian,
    };
  }, [dateRange, listLibur, dataDaftarHadir]);

  const columns = [
    { Header: "No", accessor: "index", width: "4%" },
    { Header: "Tanggal", accessor: "tanggal", width: "9%" },
    {
      Header: "Waktu Masuk & Apel Pagi",
      accessor: "masuk_apel_pagi",
      width: "14%",
    },
    {
      Header: "Laporan Harian",
      accessor: "laporan_harian",
      width: "15%",
    },
    { Header: "Instruksi Selesai", accessor: "instruksi", width: "11%" },
    { Header: "Disposisi Selesai", accessor: "disposisi", width: "11%" },
    { Header: "Event", accessor: "events", width: "11%" },
    { Header: "Tamu", accessor: "tamu", width: "11%" },
    {
      Header: "Waktu Pulang & Apel Siang",
      accessor: "pulang_apel_siang",
      width: "14%",
    },
  ];

  const rows = filteredLaporanHarian.map((item, index) => {
    // Cari laporan harian pertama untuk aksi (jika ada)
    const firstLaporan =
      item.laporan_harian && item.laporan_harian.length > 0
        ? item.laporan_harian[0]
        : null;

    // Cari data daftar hadir untuk tanggal ini
    const tanggalNormalized = normalizeDate(item.tanggal);
    const daftarHadirData = dataDaftarHadir.find(
      (dh) => normalizeDate(dh.tanggal) === tanggalNormalized
    );

    // Cari data hari libur untuk tanggal ini
    const hariLiburData = dataHariLibur.find(
      (hl) => normalizeDate(hl.tanggal) === tanggalNormalized
    );

    // Tentukan apakah hari Sabtu atau Minggu
    const tanggalObj = new Date(item.tanggal);
    const dayOfWeek = tanggalObj.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 = Minggu, 6 = Sabtu

    // Format tanggal dengan label
    const tanggalFormatted = new Date(item.tanggal).toLocaleDateString(
      "id-ID",
      {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      }
    );

    let tanggalLabel = tanggalFormatted;
    if (hariLiburData) {
      tanggalLabel = `${tanggalFormatted} - Libur (${hariLiburData.libur})`;
    } else if (isWeekend) {
      tanggalLabel = `${tanggalFormatted} - Bukan Hari Kerja`;
    }

    return {
      index: index + 1,
      tanggal: (
        <MDBox
          sx={{
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "100%",
          }}
        >
          <MDTypography variant="caption" color="text" display="block">
            {tanggalFormatted}
          </MDTypography>
          {hariLiburData && (
            <Chip
              label={`Libur: ${hariLiburData.libur}`}
              color="warning"
              size="small"
              sx={{ mt: 0.5, wordBreak: "break-word", whiteSpace: "normal" }}
            />
          )}
          {!hariLiburData && isWeekend && (
            <Chip
              label="Bukan Hari Kerja"
              color="secondary"
              size="small"
              sx={{ mt: 0.5 }}
            />
          )}
        </MDBox>
      ),
      masuk_apel_pagi: (
        <MDBox
          sx={{
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "100%",
          }}
        >
          {daftarHadirData?.waktuMasuk && (
            <MDBox mb={0.5}>
              <MDTypography variant="caption" color="text" display="block">
                {daftarHadirData.status === "Hadir" ||
                daftarHadirData.status === "Dinas Luar"
                  ? "Masuk"
                  : "Lapor"}
                : {daftarHadirData.waktuMasuk}
              </MDTypography>
              {daftarHadirData.status && (
                <Chip
                  label={daftarHadirData.status}
                  color={
                    daftarHadirData.status === "Hadir"
                      ? "success"
                      : daftarHadirData.status === "Izin"
                      ? "warning"
                      : "error"
                  }
                  size="small"
                  variant="filled"
                  sx={{ mt: 0.5 }}
                />
              )}
            </MDBox>
          )}
          {item.apel_pagi ? (
            <MDTypography
              variant="caption"
              color="text"
              display="block"
              mt={0.5}
            >
              Apel: {item.apel_pagi.jam_absen || "-"}
            </MDTypography>
          ) : (
            <MDTypography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              Belum Apel Pagi
            </MDTypography>
          )}
        </MDBox>
      ),
      laporan_harian: (
        <MDBox
          sx={{
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "100%",
          }}
        >
          {item.laporan_harian && item.laporan_harian.length > 0 ? (
            <MDBox>
              <MDBox mb={1}>
                <Chip
                  label={`${item.laporan_harian.length} Laporan`}
                  color="info"
                  size="small"
                  sx={{ mb: 0.5, cursor: "pointer" }}
                  onClick={() =>
                    handleOpenDetailModal(
                      "laporan_harian",
                      item.laporan_harian,
                      item.tanggal
                    )
                  }
                />
                <MDTypography variant="caption" color="text" display="block">
                  {item.laporan_harian.reduce(
                    (sum, l) => sum + (l.jumlah_kegiatan || 0),
                    0
                  )}{" "}
                  Kegiatan
                </MDTypography>
              </MDBox>
              {firstLaporan && (
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() =>
                    handleOpenKegiatanModal(
                      firstLaporan.id_laporan,
                      item.tanggal
                    )
                  }
                >
                  Lihat Kegiatan
                </MDButton>
              )}
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          )}
        </MDBox>
      ),
      instruksi: (
        <MDBox
          sx={{
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "100%",
          }}
        >
          {(() => {
            const instruksiTerima =
              item.instruksi?.filter((i) => i.jenis === "terima") || [];
            const instruksiBeri =
              item.instruksi?.filter((i) => i.jenis === "beri") || [];
            const totalInstruksi = item.instruksi?.length || 0;

            if (totalInstruksi === 0) {
              return (
                <MDTypography variant="caption" color="text.secondary">
                  -
                </MDTypography>
              );
            }

            return (
              <MDBox>
                {instruksiTerima.length > 0 && (
                  <MDBox mb={0.5}>
                    <Chip
                      label={`Terima: ${instruksiTerima.length}`}
                      color="success"
                      size="small"
                      sx={{ mb: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        handleOpenDetailModal(
                          "instruksi",
                          instruksiTerima,
                          item.tanggal
                        )
                      }
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
                    >
                      {instruksiTerima[0].perihal || "-"}
                    </MDTypography>
                  </MDBox>
                )}
                {instruksiBeri.length > 0 && (
                  <MDBox>
                    <Chip
                      label={`Beri: ${instruksiBeri.length}`}
                      color="info"
                      size="small"
                      sx={{ mb: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        handleOpenDetailModal(
                          "instruksi",
                          instruksiBeri,
                          item.tanggal
                        )
                      }
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
                    >
                      {instruksiBeri[0].perihal || "-"}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            );
          })()}
        </MDBox>
      ),
      disposisi: (
        <MDBox
          sx={{
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "100%",
          }}
        >
          {(() => {
            const disposisiTerima =
              item.disposisi?.filter((d) => d.jenis === "terima") || [];
            const disposisiBeri =
              item.disposisi?.filter((d) => d.jenis === "beri") || [];
            const totalDisposisi = item.disposisi?.length || 0;

            if (totalDisposisi === 0) {
              return (
                <MDTypography variant="caption" color="text.secondary">
                  -
                </MDTypography>
              );
            }

            return (
              <MDBox>
                {disposisiTerima.length > 0 && (
                  <MDBox mb={0.5}>
                    <Chip
                      label={`Terima: ${disposisiTerima.length}`}
                      color="warning"
                      size="small"
                      sx={{ mb: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        handleOpenDetailModal(
                          "disposisi",
                          disposisiTerima,
                          item.tanggal
                        )
                      }
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
                    >
                      {disposisiTerima[0].perihal ||
                        disposisiTerima[0].nomor_surat ||
                        "-"}
                    </MDTypography>
                  </MDBox>
                )}
                {disposisiBeri.length > 0 && (
                  <MDBox>
                    <Chip
                      label={`Beri: ${disposisiBeri.length}`}
                      color="info"
                      size="small"
                      sx={{ mb: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        handleOpenDetailModal(
                          "disposisi",
                          disposisiBeri,
                          item.tanggal
                        )
                      }
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
                    >
                      {disposisiBeri[0].perihal ||
                        disposisiBeri[0].nomor_surat ||
                        "-"}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            );
          })()}
        </MDBox>
      ),
      events: (
        <MDBox
          sx={{
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "100%",
          }}
        >
          {item.events && item.events.length > 0 ? (
            <MDBox>
              <Chip
                label={`${item.events.length} Event`}
                color="primary"
                size="small"
                sx={{ mb: 0.5, cursor: "pointer" }}
                onClick={() =>
                  handleOpenDetailModal("events", item.events, item.tanggal)
                }
              />
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
              >
                {item.events[0].nama_event || "-"}
              </MDTypography>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          )}
        </MDBox>
      ),
      tamu: (
        <MDBox
          sx={{
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "100%",
          }}
        >
          {item.tamu && item.tamu.length > 0 ? (
            <MDBox>
              <Chip
                label={`${item.tamu.length} Tamu`}
                color="secondary"
                size="small"
                sx={{ mb: 0.5, cursor: "pointer" }}
                onClick={() =>
                  handleOpenDetailModal("tamu", item.tamu, item.tanggal)
                }
              />
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
              >
                {item.tamu[0].nama_tamu || "-"}
              </MDTypography>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          )}
        </MDBox>
      ),
      pulang_apel_siang: (
        <MDBox
          sx={{
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "100%",
          }}
        >
          {daftarHadirData?.waktuPulang && (
            <MDBox mb={0.5}>
              <MDTypography variant="caption" color="text" display="block">
                Pulang: {daftarHadirData.waktuPulang}
              </MDTypography>
              {daftarHadirData.workingHours && (
                <>
                  <MDTypography
                    variant="caption"
                    color="info"
                    display="block"
                    sx={{ fontWeight: "bold", mt: 0.5 }}
                  >
                    Jam Kerja: {daftarHadirData.workingHours}
                  </MDTypography>
                  {/* Progress bar untuk capaian jam kerja harian */}
                  {(() => {
                    const targetJamKerja = getTargetJamKerja(item.tanggal);
                    const percentage = calculatePercentage(
                      daftarHadirData.workingHours,
                      item.tanggal
                    );
                    return (
                      <MDBox mt={1}>
                        <MDBox display="flex" alignItems="center" mb={0.5}>
                          <MDProgress
                            value={percentage}
                            color={
                              percentage >= 80
                                ? "success"
                                : percentage >= 60
                                ? "warning"
                                : "error"
                            }
                            variant="gradient"
                            sx={{ flexGrow: 1, mr: 1 }}
                          />
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            color="text"
                          >
                            {percentage}%
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" color="text.secondary">
                          Target: {targetJamKerja}
                        </MDTypography>
                      </MDBox>
                    );
                  })()}
                </>
              )}
            </MDBox>
          )}
          {item.apel_siang ? (
            <MDTypography
              variant="caption"
              color="text"
              display="block"
              mt={0.5}
            >
              Apel: {item.apel_siang.jam_absen || "-"}
            </MDTypography>
          ) : (
            <MDTypography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              Belum Apel Siang
            </MDTypography>
          )}
        </MDBox>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
              >
                <MDBox>
                  <MDTypography variant="h6" gutterBottom>
                    Detail Kinerja Individu
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    {pegawaiData?.nama || "Loading..."}
                    {pegawaiData?.nama_jabatan &&
                      ` - ${pegawaiData.nama_jabatan}`}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="info"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Menampilkan data dari{" "}
                    {filterDateRange.tanggal_awal
                      ? new Date(
                          filterDateRange.tanggal_awal
                        ).toLocaleDateString("id-ID")
                      : "..."}{" "}
                    hingga{" "}
                    {filterDateRange.tanggal_akhir
                      ? new Date(
                          filterDateRange.tanggal_akhir
                        ).toLocaleDateString("id-ID")
                      : "..."}
                    {filterDateRange.tanggal_awal &&
                      filterDateRange.tanggal_akhir && (
                        <span style={{ color: "#2196f3", fontWeight: "bold" }}>
                          {" "}
                          (Filter Aktif)
                        </span>
                      )}
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate(-1)}
                  startIcon={<Icon>arrow_back</Icon>}
                >
                  Kembali
                </MDButton>
              </MDBox>
            </Card>
          </Grid>

          {/* Card Statistik Aktivitas */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" gutterBottom mb={2}>
                  Ringkasan Aktivitas
                </MDTypography>
                <MDBox display="flex" flexWrap="wrap" gap={1.5}>
                  <Chip
                    label={`Total Hadir: ${statistikAktivitas.totalHadir}`}
                    color="success"
                    size="medium"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                  <Chip
                    label={`Apel Pagi: ${statistikAktivitas.totalApelPagi}`}
                    color="info"
                    size="medium"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                  <Chip
                    label={`Apel Siang: ${statistikAktivitas.totalApelSiang}`}
                    color="warning"
                    size="medium"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                  <Chip
                    label={`Disposisi: ${statistikAktivitas.totalDisposisi}`}
                    color="warning"
                    size="medium"
                    variant="outlined"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                  <Chip
                    label={`Instruksi: ${statistikAktivitas.totalInstruksi}`}
                    color="success"
                    size="medium"
                    variant="outlined"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                  <Chip
                    label={`Tamu: ${statistikAktivitas.totalTamu}`}
                    color="secondary"
                    size="medium"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                  <Chip
                    label={`Event: ${statistikAktivitas.totalEvent}`}
                    color="primary"
                    size="medium"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Card Statistik Jam Kerja */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" gutterBottom mb={2}>
                  Statistik Jam Kerja
                </MDTypography>
                <MDBox display="flex" flexWrap="wrap" gap={1.5} mb={2}>
                  <Chip
                    icon={<Icon>calendar_today</Icon>}
                    label={`Total Hari Kerja: ${statistikJamKerja.totalHariKerja}`}
                    color="primary"
                    size="medium"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                  <Chip
                    icon={<Icon>schedule</Icon>}
                    label={`Target Jam Kerja: ${statistikJamKerja.targetJamKerja}`}
                    color="info"
                    size="medium"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                  <Chip
                    icon={<Icon>access_time</Icon>}
                    label={`Total Jam Kerja Aktual: ${statistikJamKerja.totalJamKerjaAktual}`}
                    color={
                      statistikJamKerja.persentasePencapaian >= 80
                        ? "success"
                        : statistikJamKerja.persentasePencapaian >= 60
                        ? "warning"
                        : "error"
                    }
                    size="medium"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                  <Chip
                    icon={<Icon>trending_up</Icon>}
                    label={`Persentase Pencapaian: ${statistikJamKerja.persentasePencapaian}%`}
                    color={
                      statistikJamKerja.persentasePencapaian >= 80
                        ? "success"
                        : statistikJamKerja.persentasePencapaian >= 60
                        ? "warning"
                        : "error"
                    }
                    size="medium"
                    variant="outlined"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold", px: 1 }}
                  />
                </MDBox>
                <MDBox>
                  <MDBox display="flex" alignItems="center" mb={1}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ mr: 2, minWidth: 150 }}
                    >
                      Progress Pencapaian:
                    </MDTypography>
                    <MDProgress
                      value={statistikJamKerja.persentasePencapaian}
                      color={
                        statistikJamKerja.persentasePencapaian >= 80
                          ? "success"
                          : statistikJamKerja.persentasePencapaian >= 60
                          ? "warning"
                          : "error"
                      }
                      variant="gradient"
                      sx={{ flexGrow: 1, mr: 1 }}
                    />
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color="text"
                      sx={{ minWidth: 50 }}
                    >
                      {statistikJamKerja.persentasePencapaian}%
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text.secondary">
                    {statistikJamKerja.totalJamKerjaAktual} /{" "}
                    {statistikJamKerja.targetJamKerja} jam
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container spacing={2} alignItems="center" mb={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Tanggal Awal"
                      value={filterDateRange.tanggal_awal}
                      onChange={(e) =>
                        handleDateRangeChange("tanggal_awal", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Tanggal Akhir"
                      value={filterDateRange.tanggal_akhir}
                      onChange={(e) =>
                        handleDateRangeChange("tanggal_akhir", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={handleResetFilter}
                      fullWidth
                    >
                      Reset Filter
                    </MDButton>
                  </Grid>
                </Grid>

                {isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {loading && (
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={4}
                      >
                        <CircularProgress />
                      </MDBox>
                    )}
                    {!loading && filteredLaporanHarian.length === 0 && (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        py={4}
                      >
                        <Icon
                          sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                        >
                          description
                        </Icon>
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data
                        </MDTypography>
                      </MDBox>
                    )}
                    {!loading &&
                      filteredLaporanHarian.length > 0 &&
                      filteredLaporanHarian.map((item, index) => {
                        const firstLaporan =
                          item.laporan_harian && item.laporan_harian.length > 0
                            ? item.laporan_harian[0]
                            : null;
                        const totalKegiatan = item.laporan_harian
                          ? item.laporan_harian.reduce(
                              (sum, l) => sum + (l.jumlah_kegiatan || 0),
                              0
                            )
                          : 0;

                        // Cari data daftar hadir untuk tanggal ini
                        const tanggalNormalized = normalizeDate(item.tanggal);
                        const daftarHadirData = dataDaftarHadir.find(
                          (dh) =>
                            normalizeDate(dh.tanggal) === tanggalNormalized
                        );

                        // Cari data hari libur untuk tanggal ini
                        const hariLiburData = dataHariLibur.find(
                          (hl) =>
                            normalizeDate(hl.tanggal) === tanggalNormalized
                        );

                        // Tentukan apakah hari Sabtu atau Minggu
                        const tanggalObj = new Date(item.tanggal);
                        const dayOfWeek = tanggalObj.getDay();
                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 = Minggu, 6 = Sabtu

                        return (
                          <Card key={`laporan-${item.tanggal || index}`}>
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1}
                                  >
                                    <MDTypography
                                      variant="button"
                                      fontWeight="bold"
                                    >
                                      #{index + 1}
                                    </MDTypography>
                                    <MDBox>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {new Date(
                                          item.tanggal
                                        ).toLocaleDateString("id-ID", {
                                          weekday: "long",
                                          year: "numeric",
                                          month: "long",
                                          day: "numeric",
                                        })}
                                      </MDTypography>
                                      {hariLiburData && (
                                        <Chip
                                          label={`Libur: ${hariLiburData.libur}`}
                                          color="warning"
                                          size="small"
                                          sx={{ mt: 0.5 }}
                                        />
                                      )}
                                      {!hariLiburData && isWeekend && (
                                        <Chip
                                          label="Bukan Hari Kerja"
                                          color="secondary"
                                          size="small"
                                          sx={{ mt: 0.5 }}
                                        />
                                      )}
                                    </MDBox>
                                  </MDBox>
                                </Grid>

                                {/* Waktu Masuk & Apel Pagi */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#e8f5e9",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="success"
                                    >
                                      Masuk & Apel Pagi
                                    </MDTypography>
                                    {daftarHadirData?.waktuMasuk && (
                                      <MDBox mt={0.5}>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          {daftarHadirData.status === "Hadir" ||
                                          daftarHadirData.status ===
                                            "Dinas Luar"
                                            ? "Masuk"
                                            : "Lapor"}
                                          : {daftarHadirData.waktuMasuk}
                                        </MDTypography>
                                        {daftarHadirData.status && (
                                          <Chip
                                            label={daftarHadirData.status}
                                            color={
                                              daftarHadirData.status === "Hadir"
                                                ? "success"
                                                : daftarHadirData.status ===
                                                  "Izin"
                                                ? "warning"
                                                : "error"
                                            }
                                            size="small"
                                            variant="filled"
                                            sx={{ mt: 0.5 }}
                                          />
                                        )}
                                      </MDBox>
                                    )}
                                    {item.apel_pagi ? (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mt: 0.5 }}
                                      >
                                        Jam Apel:{" "}
                                        {item.apel_pagi.jam_absen || "-"}
                                      </MDTypography>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Belum Apel Pagi
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>

                                {/* Laporan Harian */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#e3f2fd",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="info"
                                    >
                                      Laporan Harian
                                    </MDTypography>
                                    {item.laporan_harian &&
                                    item.laporan_harian.length > 0 ? (
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${item.laporan_harian.length} Laporan - ${totalKegiatan} Kegiatan`}
                                          color="info"
                                          size="small"
                                          sx={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "laporan_harian",
                                              item.laporan_harian,
                                              item.tanggal
                                            )
                                          }
                                        />
                                        {firstLaporan && (
                                          <MDButton
                                            variant="outlined"
                                            color="info"
                                            size="small"
                                            sx={{ mt: 1 }}
                                            onClick={() =>
                                              handleOpenKegiatanModal(
                                                firstLaporan.id_laporan,
                                                item.tanggal
                                              )
                                            }
                                          >
                                            Lihat Kegiatan
                                          </MDButton>
                                        )}
                                      </MDBox>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Tidak ada laporan
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>

                                {/* Instruksi */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#e8f5e9",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="success"
                                    >
                                      Instruksi
                                    </MDTypography>
                                    {(() => {
                                      const instruksiTerima =
                                        item.instruksi?.filter(
                                          (i) => i.jenis === "terima"
                                        ) || [];
                                      const instruksiBeri =
                                        item.instruksi?.filter(
                                          (i) => i.jenis === "beri"
                                        ) || [];
                                      const totalInstruksi =
                                        item.instruksi?.length || 0;

                                      if (totalInstruksi === 0) {
                                        return (
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.5 }}
                                          >
                                            Tidak ada instruksi
                                          </MDTypography>
                                        );
                                      }

                                      return (
                                        <MDBox mt={0.5}>
                                          {instruksiTerima.length > 0 && (
                                            <MDBox mb={1}>
                                              <Chip
                                                label={`Terima: ${instruksiTerima.length}`}
                                                color="success"
                                                size="small"
                                                sx={{
                                                  mb: 0.5,
                                                  cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                  handleOpenDetailModal(
                                                    "instruksi",
                                                    instruksiTerima,
                                                    item.tanggal
                                                  )
                                                }
                                              />
                                              {instruksiTerima
                                                .slice(0, 2)
                                                .map((inst, idx) => (
                                                  <MDTypography
                                                    key={idx}
                                                    variant="caption"
                                                    color="text"
                                                    display="block"
                                                  >
                                                    • {inst.perihal || "-"}
                                                  </MDTypography>
                                                ))}
                                              {instruksiTerima.length > 2 && (
                                                <MDTypography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  +{instruksiTerima.length - 2}{" "}
                                                  lainnya
                                                </MDTypography>
                                              )}
                                            </MDBox>
                                          )}
                                          {instruksiBeri.length > 0 && (
                                            <MDBox>
                                              <Chip
                                                label={`Beri: ${instruksiBeri.length}`}
                                                color="info"
                                                size="small"
                                                sx={{
                                                  mb: 0.5,
                                                  cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                  handleOpenDetailModal(
                                                    "instruksi",
                                                    instruksiBeri,
                                                    item.tanggal
                                                  )
                                                }
                                              />
                                              {instruksiBeri
                                                .slice(0, 2)
                                                .map((inst, idx) => (
                                                  <MDTypography
                                                    key={idx}
                                                    variant="caption"
                                                    color="text"
                                                    display="block"
                                                  >
                                                    • {inst.perihal || "-"}
                                                  </MDTypography>
                                                ))}
                                              {instruksiBeri.length > 2 && (
                                                <MDTypography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  +{instruksiBeri.length - 2}{" "}
                                                  lainnya
                                                </MDTypography>
                                              )}
                                            </MDBox>
                                          )}
                                        </MDBox>
                                      );
                                    })()}
                                  </MDBox>
                                </Grid>

                                {/* Disposisi */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#fff3e0",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="warning"
                                    >
                                      Disposisi
                                    </MDTypography>
                                    {(() => {
                                      const disposisiTerima =
                                        item.disposisi?.filter(
                                          (d) => d.jenis === "terima"
                                        ) || [];
                                      const disposisiBeri =
                                        item.disposisi?.filter(
                                          (d) => d.jenis === "beri"
                                        ) || [];
                                      const totalDisposisi =
                                        item.disposisi?.length || 0;

                                      if (totalDisposisi === 0) {
                                        return (
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.5 }}
                                          >
                                            Tidak ada disposisi
                                          </MDTypography>
                                        );
                                      }

                                      return (
                                        <MDBox mt={0.5}>
                                          {disposisiTerima.length > 0 && (
                                            <MDBox mb={1}>
                                              <Chip
                                                label={`Terima: ${disposisiTerima.length}`}
                                                color="warning"
                                                size="small"
                                                sx={{
                                                  mb: 0.5,
                                                  cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                  handleOpenDetailModal(
                                                    "disposisi",
                                                    disposisiTerima,
                                                    item.tanggal
                                                  )
                                                }
                                              />
                                              {disposisiTerima
                                                .slice(0, 2)
                                                .map((disp, idx) => (
                                                  <MDTypography
                                                    key={idx}
                                                    variant="caption"
                                                    color="text"
                                                    display="block"
                                                  >
                                                    •{" "}
                                                    {disp.perihal ||
                                                      disp.nomor_surat ||
                                                      "-"}
                                                  </MDTypography>
                                                ))}
                                              {disposisiTerima.length > 2 && (
                                                <MDTypography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  +{disposisiTerima.length - 2}{" "}
                                                  lainnya
                                                </MDTypography>
                                              )}
                                            </MDBox>
                                          )}
                                          {disposisiBeri.length > 0 && (
                                            <MDBox>
                                              <Chip
                                                label={`Beri: ${disposisiBeri.length}`}
                                                color="info"
                                                size="small"
                                                sx={{
                                                  mb: 0.5,
                                                  cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                  handleOpenDetailModal(
                                                    "disposisi",
                                                    disposisiBeri,
                                                    item.tanggal
                                                  )
                                                }
                                              />
                                              {disposisiBeri
                                                .slice(0, 2)
                                                .map((disp, idx) => (
                                                  <MDTypography
                                                    key={idx}
                                                    variant="caption"
                                                    color="text"
                                                    display="block"
                                                  >
                                                    •{" "}
                                                    {disp.perihal ||
                                                      disp.nomor_surat ||
                                                      "-"}
                                                  </MDTypography>
                                                ))}
                                              {disposisiBeri.length > 2 && (
                                                <MDTypography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  +{disposisiBeri.length - 2}{" "}
                                                  lainnya
                                                </MDTypography>
                                              )}
                                            </MDBox>
                                          )}
                                        </MDBox>
                                      );
                                    })()}
                                  </MDBox>
                                </Grid>

                                {/* Events */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#f3e5f5",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="primary"
                                    >
                                      Event
                                    </MDTypography>
                                    {item.events && item.events.length > 0 ? (
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${item.events.length} Event`}
                                          color="primary"
                                          size="small"
                                          sx={{ mb: 0.5, cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "events",
                                              item.events,
                                              item.tanggal
                                            )
                                          }
                                        />
                                        {item.events
                                          .slice(0, 2)
                                          .map((evt, idx) => (
                                            <MDTypography
                                              key={idx}
                                              variant="caption"
                                              color="text"
                                              display="block"
                                            >
                                              • {evt.nama_event || "-"}
                                            </MDTypography>
                                          ))}
                                        {item.events.length > 2 && (
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            +{item.events.length - 2} lainnya
                                          </MDTypography>
                                        )}
                                      </MDBox>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Tidak ada event
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>

                                {/* Tamu */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#fce4ec",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="secondary"
                                    >
                                      Tamu
                                    </MDTypography>
                                    {item.tamu && item.tamu.length > 0 ? (
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${item.tamu.length} Tamu`}
                                          color="secondary"
                                          size="small"
                                          sx={{ mb: 0.5, cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "tamu",
                                              item.tamu,
                                              item.tanggal
                                            )
                                          }
                                        />
                                        {item.tamu
                                          .slice(0, 2)
                                          .map((tamu, idx) => (
                                            <MDTypography
                                              key={idx}
                                              variant="caption"
                                              color="text"
                                              display="block"
                                            >
                                              • {tamu.nama_tamu || "-"}
                                            </MDTypography>
                                          ))}
                                        {item.tamu.length > 2 && (
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            +{item.tamu.length - 2} lainnya
                                          </MDTypography>
                                        )}
                                      </MDBox>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Tidak ada tamu
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>

                                {/* Waktu Pulang & Apel Siang */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#fff3e0",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="warning"
                                    >
                                      Pulang & Apel Siang
                                    </MDTypography>
                                    {daftarHadirData?.waktuPulang && (
                                      <MDBox mt={0.5}>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          Waktu Pulang:{" "}
                                          {daftarHadirData.waktuPulang}
                                        </MDTypography>
                                        {daftarHadirData.workingHours && (
                                          <>
                                            <Chip
                                              label={`Jam Kerja: ${daftarHadirData.workingHours}`}
                                              color="info"
                                              size="small"
                                              variant="filled"
                                              sx={{ mt: 0.5 }}
                                            />
                                            {/* Progress bar untuk capaian jam kerja harian */}
                                            {(() => {
                                              const targetJamKerja =
                                                getTargetJamKerja(item.tanggal);
                                              const percentage =
                                                calculatePercentage(
                                                  daftarHadirData.workingHours,
                                                  item.tanggal
                                                );
                                              return (
                                                <MDBox mt={1}>
                                                  <MDBox
                                                    display="flex"
                                                    alignItems="center"
                                                    mb={0.5}
                                                  >
                                                    <MDProgress
                                                      value={percentage}
                                                      color={
                                                        percentage >= 80
                                                          ? "success"
                                                          : percentage >= 60
                                                          ? "warning"
                                                          : "error"
                                                      }
                                                      variant="gradient"
                                                      sx={{
                                                        flexGrow: 1,
                                                        mr: 1,
                                                      }}
                                                    />
                                                    <MDTypography
                                                      variant="caption"
                                                      fontWeight="bold"
                                                      color="text"
                                                    >
                                                      {percentage}%
                                                    </MDTypography>
                                                  </MDBox>
                                                  <MDTypography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    Target: {targetJamKerja}
                                                  </MDTypography>
                                                </MDBox>
                                              );
                                            })()}
                                          </>
                                        )}
                                      </MDBox>
                                    )}
                                    {item.apel_siang ? (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mt: 0.5 }}
                                      >
                                        Jam Apel:{" "}
                                        {item.apel_siang.jam_absen || "-"}
                                      </MDTypography>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Belum Apel Siang
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    loading={loading}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Modal Kegiatan */}
        <Dialog
          open={openKegiatanModal}
          onClose={handleCloseKegiatanModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">{selectedLaporanTitle}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {loadingKegiatan ? (
                <MDBox textAlign="center" py={4}>
                  <CircularProgress />
                </MDBox>
              ) : selectedKegiatan.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Tidak ada kegiatan untuk ditampilkan
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  <MDBox mb={3}>
                    <MDTypography variant="h6" color="info" gutterBottom>
                      Total Kegiatan: {selectedKegiatan.length}
                    </MDTypography>
                  </MDBox>
                  <Grid container spacing={2}>
                    {selectedKegiatan.map((kegiatan, index) => (
                      <Grid item xs={12} key={index}>
                        <Card sx={{ p: 2, mb: 2 }}>
                          <MDBox>
                            <MDTypography
                              variant="h6"
                              color="info"
                              gutterBottom
                              mb={2}
                            >
                              Kegiatan {index + 1}
                            </MDTypography>

                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Deskripsi Kegiatan:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.kegiatan}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Jumlah Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.jumlah_output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Satuan Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.satuan_output}
                                </MDTypography>
                              </Grid>
                            </Grid>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseKegiatanModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Dialog Detail Modal */}
        <Dialog
          open={openDetailModal}
          onClose={handleCloseDetailModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">{detailModalData.title}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {detailModalData.data.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Tidak ada data untuk ditampilkan
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  {detailModalData.type === "laporan_harian" && (
                    <MDBox>
                      {detailModalData.data.map((laporan, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="info"
                              gutterBottom
                            >
                              Laporan #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Tanggal:{" "}
                              {new Date(laporan.tanggal).toLocaleDateString(
                                "id-ID"
                              )}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Jumlah Kegiatan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {laporan.jumlah_kegiatan || 0}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                ID Laporan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {laporan.id_laporan}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "instruksi" && (
                    <MDBox>
                      {detailModalData.data.map((instruksi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="success"
                              gutterBottom
                            >
                              Instruksi #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Selesai:{" "}
                              {instruksi.waktu_selesai
                                ? new Date(
                                    instruksi.waktu_selesai
                                  ).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Perihal:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {instruksi.perihal || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Isi Instruksi:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {instruksi.isi_instruksi || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Prioritas:
                              </MDTypography>
                              <Chip
                                label={instruksi.prioritas || "-"}
                                color={
                                  instruksi.prioritas === "Tinggi"
                                    ? "error"
                                    : instruksi.prioritas === "Sedang"
                                    ? "warning"
                                    : "default"
                                }
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Pemberi Perintah:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {instruksi.pemberi_perintah || "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "disposisi" && (
                    <MDBox>
                      {detailModalData.data.map((disposisi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="warning"
                              gutterBottom
                            >
                              Disposisi #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Selesai:{" "}
                              {disposisi.waktu_selesai
                                ? new Date(
                                    disposisi.waktu_selesai
                                  ).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Perihal:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {disposisi.perihal || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Nomor Surat:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {disposisi.nomor_surat || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Pemberi Perintah:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {disposisi.pemberi_perintah || "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "events" && (
                    <MDBox>
                      {detailModalData.data.map((event, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="primary"
                              gutterBottom
                            >
                              Event #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Waktu Absen:{" "}
                              {event.waktu
                                ? new Date(event.waktu).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Nama Event:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.nama_event || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Detail Kegiatan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.detail_kegiatan || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Tempat:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.tempat || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Waktu Mulai:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.waktu_mulai
                                  ? new Date(event.waktu_mulai).toLocaleString(
                                      "id-ID"
                                    )
                                  : "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Waktu Selesai:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.waktu_finish
                                  ? new Date(event.waktu_finish).toLocaleString(
                                      "id-ID"
                                    )
                                  : "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "tamu" && (
                    <MDBox>
                      {detailModalData.data.map((tamu, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="secondary"
                              gutterBottom
                            >
                              Tamu #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Waktu Kedatangan:{" "}
                              {tamu.waktu_kedatangan
                                ? new Date(
                                    tamu.waktu_kedatangan
                                  ).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Nama Tamu:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.nama_tamu || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Instansi:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.nama_instansi || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Keperluan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.keperluan || "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDetailModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DetailKinerjaIndividu;
